import { ActiveDocument } from "@entities/common";
import { type ComboboxOption } from "@ubud-ui/react-ui-kit";
import { z } from "zod";
import { EmployerStatus } from "../types";

export const governmentEmployerBasicRegistrationSchema = z.object({
  government_agency_id: z.looseObject<ComboboxOption>({} as ComboboxOption, "Pilih lembaga/instansi terlebih dahulu"),
  name: z.string("Isi nama entitas terlebih dahulu").min(1, "Isi nama entitas terlebih dahulu"),
  establishment_date: z.date("Isi tanggal berdiri"),
  phone: z
    .string("Isi nomor telepon entitas terlebih dahulu")
    .min(10, "Panjang nomor telepon minimal: 10 karakter")
    .regex(/^[0-9]+$/, "Nomor telepon tidak valid"),
  email: z.email("Format email tidak tepat").min(1, "Isi email resmi entitas terlebih dahulu"),
  website: z.string("Isi situs web terlebih dahulu").min(1, "Isi situs web terlebih dahulu"),
  description: z.string("Isi deskripsi singkat entitas terlebih dahulu").min(1, "Isi deskripsi singkat entitas terlebih dahulu"),
  logo: z.string("Upload logo entitas terlebih dahulu").min(1, "Upload logo entitas terlebih dahulu"),
});
export type GovernmentEmployerBasicRegistrationDto = z.infer<typeof governmentEmployerBasicRegistrationSchema>;

export const governmentEmployerLocationRegistrationSchema = z.object({
  province_id: z.looseObject<ComboboxOption>({} as ComboboxOption, "Pilih provinsi terlebih dahulu"),
  city_id: z.looseObject<ComboboxOption>({} as ComboboxOption, "Pilih kabupaten/kota terlebih dahulu"),
  sub_district_id: z.looseObject<ComboboxOption>({} as ComboboxOption, "Pilih kecamatan terlebih dahulu"),
  village_id: z.looseObject<ComboboxOption>({} as ComboboxOption, "Pilih kelurahan terlebih dahulu"),
  address: z.string("Isi alamat lengkap entitas terlebih dahulu").min(1, "Isi alamat lengkap entitas terlebih dahulu").optional(),
  postal_code: z
    .string("Isi kode pos terlebih dahulu")
    .min(5, "Isi kode pos terlebih dahulu")
    .max(5, "Maksimal 5 digit")
    .regex(/^[0-9]+$/, "Kode pos tidak valid"),
  latitude: z.number("Pilih latitude terlebih dahulu"),
  longitude: z.number("Pilih longitude terlebih dahulu"),
});
export type GovernmentEmployerLocationRegistrationDto = z.infer<typeof governmentEmployerLocationRegistrationSchema>;

export const governmentEmployerPicRegistrationSchema = z.object({
  pic_name: z.string("Nama PIC harus diisi"),
  pic_id_number: z.string("NIK PIC harus diisi"),
  pic_email: z.email("Format email tidak tepat").min(1, "Email PIC harus diisi"),
  pic_phone: z
    .string("Nomor telepon PIC harus diisi")
    .regex(/^[0-9]+$/, "Nomor telepon tidak valid"),
});
export type GovernmentEmployerPicRegistrationDto = z.infer<typeof governmentEmployerPicRegistrationSchema>;

export type GovernmentEmployerDocumentRegistrationItem = {
  documentId: string;
  fileId: string;
};

export type GovernmentEmployerDocumentRegistrationDto = {
  documents: GovernmentEmployerDocumentRegistrationItem[];
};

export type GovernmentEmployerDocumentFormValues = {
  files: Record<string, string>;
};

export const buildGovernmentEmployerDocumentRegistrationSchema = (documents: ActiveDocument[]) => {
  return z
    .object({
      files: z.record(z.string(), z.string()),
    })
    .superRefine((value, ctx) => {
      documents.forEach((document) => {
        if (document.required && !value.files[document.id]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Upload ${document.name} terlebih dahulu`,
            path: ["files", document.id],
          });
        }
      });
    });
};

export interface GovernmentEmployerRegistrationResponse {
  data: {
    id: string;
    userId: string;
    slug: string;
    name: string;
    status: EmployerStatus;
  };
}
